// ═══════════════════════════════════════════════════
//  Per-product fabric piece generation.
//  Turns a slot + product config into the cut-piece list
//  that gets fed into packFabricPieces.
//  See spec: docs/superpowers/specs/2026-04-14-fabric-roll-packing-design.md
// ═══════════════════════════════════════════════════

import type { Piece } from './fabric-packing';
import type { PanelRole } from '../schemas/banquette-base-template.schema';

export type FabricProductType =
  | 'banquette_back_cushion'
  | 'banquette_base'
  | 'banquette_seat_cushion'
  | 'banquette_arm';

export interface SlotInputs {
  slot_id: string;
  slot_name: string;
  panel_role: PanelRole;
  /** Real inches of the polyline drawn on the slot (resolved via image PPI). Used by 'front' role only. */
  polyline_length_in: number;
  /** Default 1". Split half-and-half across the seamed edges. */
  staple_allowance_in: number;
  /** Explicit wrap value in inches. null -> use the product default. Used by 'side' role only. */
  fabric_wrap_value: number | null;
}

export interface ProductInputs {
  product_type: FabricProductType;
  /** Number of independent cushion sections in the product. >=1. */
  section_count: number;
  /** Per-section LF in inches. Typically (lf_quantity / section_count) * 12. */
  section_lf_in: number;
  /** Profile height in inches. Used for back cushion + base side-panel "other dim"; seat cushion wrap default. */
  profile_height_in: number;
  /** Profile depth in inches. Used for back cushion wrap default; seat cushion side-panel "other dim". */
  profile_depth_in: number;
  /** Arm only: thickness side to side, inches. */
  width_in?: number;
  /** Arm only: front-to-back run, inches. This is the arm's design axis. */
  depth_in?: number;
  /** Arm only: height, inches. */
  height_in?: number;
}

/** Resolve the per-product wrap dim (inches) used for side panels. */
export function resolveWrap(slot: SlotInputs, product: ProductInputs): number {
  if (slot.fabric_wrap_value != null) return slot.fabric_wrap_value;
  switch (product.product_type) {
    case 'banquette_back_cushion':
      return product.profile_depth_in;
    case 'banquette_seat_cushion':
      return product.profile_height_in;
    case 'banquette_base':
      return 0;
    case 'banquette_arm':
      return product.width_in ?? 0;
  }
}

/** Side panel "other" dim and which axis is design-up when installed. */
function sidePanelGeometry(product: ProductInputs): {
  side_other_dim_in: number;
  side_design_up_axis: 'width' | 'length';
} {
  switch (product.product_type) {
    case 'banquette_back_cushion':
    case 'banquette_base':
      // side: (wrap+staple) x profile_height; design-up = profile_height (the "length" axis below)
      return { side_other_dim_in: product.profile_height_in, side_design_up_axis: 'length' };
    case 'banquette_seat_cushion':
      // side: (height+staple) x profile_depth; design-up = (height+staple) (the "width" axis below)
      return { side_other_dim_in: product.profile_depth_in, side_design_up_axis: 'width' };
    case 'banquette_arm':
      // end caps: (width+staple) x height; design-up = height (the "length" axis below)
      return { side_other_dim_in: product.height_in ?? 0, side_design_up_axis: 'length' };
  }
}

/**
 * Generate the cut-piece list for a fabric slot, given product context.
 * Emission depends on slot.panel_role:
 *   - 'front': 1 front per section, (section_lf + staple) × polyline_length
 *   - 'side': 2 sides per section, (wrap + staple) × side_other_dim. Skipped if wrap = 0.
 *   - 'back': 1 back per section, (section_lf + staple) × (profile_height + staple)
 */
export function generateFabricPieces(slot: SlotInputs, product: ProductInputs): Piece[] {
  const pieces: Piece[] = [];
  const staple = slot.staple_allowance_in;

  // Arms are discrete units, not runs: one piece per role (two for sides),
  // and the DEPTH is the design axis rather than the width.
  if (product.product_type === 'banquette_arm') {
    const depth = product.depth_in ?? 0;
    const height = product.height_in ?? 0;
    const wrap = resolveWrap(slot, product);

    switch (slot.panel_role) {
      case 'front':
        pieces.push({
          slot_id: slot.slot_id,
          slot_name: slot.slot_name,
          panel_type: 'front',
          section_index: 0,
          width_in: depth + staple,
          length_in: slot.polyline_length_in,
          design_up_axis: 'width',
        });
        break;
      case 'side':
        if (wrap <= 0) break;
        for (let j = 0; j < 2; j++) {
          pieces.push({
            slot_id: slot.slot_id,
            slot_name: slot.slot_name,
            panel_type: 'side',
            section_index: 0,
            panel_index: j,
            width_in: wrap + staple,
            length_in: height,
            design_up_axis: 'length',
          });
        }
        break;
      case 'back':
        pieces.push({
          slot_id: slot.slot_id,
          slot_name: slot.slot_name,
          panel_type: 'back',
          section_index: 0,
          width_in: depth + staple,
          length_in: height + staple,
          design_up_axis: 'length',
        });
        break;
    }
    return pieces;
  }

  switch (slot.panel_role) {
    case 'front': {
      const front_w = product.section_lf_in + staple;
      const front_l = slot.polyline_length_in;
      for (let i = 0; i < product.section_count; i++) {
        pieces.push({
          slot_id: slot.slot_id,
          slot_name: slot.slot_name,
          panel_type: 'front',
          section_index: i,
          width_in: front_w,
          length_in: front_l,
          design_up_axis: 'length',
        });
      }
      break;
    }
    case 'side': {
      const wrap = resolveWrap(slot, product);
      if (wrap <= 0) break;
      const { side_other_dim_in, side_design_up_axis } = sidePanelGeometry(product);
      const side_w = wrap + staple;
      const side_l = side_other_dim_in;
      for (let i = 0; i < product.section_count; i++) {
        for (let j = 0; j < 2; j++) {
          pieces.push({
            slot_id: slot.slot_id,
            slot_name: slot.slot_name,
            panel_type: 'side',
            section_index: i,
            panel_index: j,
            width_in: side_w,
            length_in: side_l,
            design_up_axis: side_design_up_axis,
          });
        }
      }
      break;
    }
    case 'back': {
      const back_w = product.section_lf_in + staple;
      const back_l = product.profile_height_in + staple;
      for (let i = 0; i < product.section_count; i++) {
        pieces.push({
          slot_id: slot.slot_id,
          slot_name: slot.slot_name,
          panel_type: 'back',
          section_index: i,
          width_in: back_w,
          length_in: back_l,
          design_up_axis: 'length',
        });
      }
      break;
    }
  }

  return pieces;
}

/**
 * Fraction of total piece area that is *not* a back panel.
 * Used to scale design_pct so back-role pieces bypass design-type allowance.
 * Approximation: assumes packed length scales proportionally to area within
 * a material group. Valid when pieces share roll width — the typical case.
 */
export function computeDesignBearingFraction(pieces: Piece[]): number {
  if (pieces.length === 0) return 0;
  let total = 0;
  let designBearing = 0;
  for (const p of pieces) {
    const area = p.width_in * p.length_in;
    total += area;
    if (p.panel_type !== 'back') designBearing += area;
  }
  return total > 0 ? designBearing / total : 0;
}
